'use client';

import { useEffect, useState } from 'react';
import styles from '@/styles/dashboard.module.css';

export function DashboardSettingsModal({ onClose }: { onClose: () => void }) {
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [ptt, setPtt] = useState(false);
  
  const [selectedAudioInput, setSelectedAudioInput] = useState('');
  const [selectedAudioOutput, setSelectedAudioOutput] = useState('');
  const [selectedVideoInput, setSelectedVideoInput] = useState('');

  useEffect(() => {
    navigator.mediaDevices?.getUserMedia({ audio: true, video: true }).then(() => {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        setAudioInputs(devices.filter((d) => d.kind === 'audioinput'));
        setAudioOutputs(devices.filter((d) => d.kind === 'audiooutput'));
        setVideoInputs(devices.filter((d) => d.kind === 'videoinput'));
      });
    }).catch(() => undefined);

    try {
      setPtt(localStorage.getItem('meetxPttMode') === 'true');
      setSelectedAudioInput(localStorage.getItem('meetxAudioInput') || '');
      setSelectedAudioOutput(localStorage.getItem('meetxAudioOutput') || '');
      setSelectedVideoInput(localStorage.getItem('meetxVideoInput') || '');
    } catch {
    }
  }, []);

  function testSpeaker() {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1);
    osc.start();
    osc.stop(ctx.currentTime + 1);
    setTimeout(() => ctx.close().catch(() => undefined), 1200);
  }

  async function testMic() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: selectedAudioInput ? { deviceId: { exact: selectedAudioInput } } : true });
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioContext();
      const analyzer = ctx.createAnalyser();
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyzer);
      analyzer.fftSize = 256;
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const checkLevel = () => {
        analyzer.getByteFrequencyData(dataArray);
        const sum = dataArray.reduce((a, b) => a + b, 0);
        const average = sum / bufferLength;
        const levelBar = document.getElementById('mic-level-bar');
        if (levelBar) {
          levelBar.style.width = `${Math.min(100, average)}%`;
        }
      };
      
      const interval = setInterval(checkLevel, 100);
      setTimeout(() => {
        clearInterval(interval);
        stream.getTracks().forEach(t => t.stop());
        ctx.close().catch(() => undefined);
        const levelBar = document.getElementById('mic-level-bar');
        if (levelBar) levelBar.style.width = '0%';
      }, 5000);
    } catch (e) {
      console.error(e);
    }
  }

  function togglePtt(value: boolean) {
    setPtt(value);
    localStorage.setItem('meetxPttMode', String(value));
    window.dispatchEvent(new Event('meetx_ptt_mode_changed'));
  }
  
  function updateDevice(type: 'audioinput' | 'audiooutput' | 'videoinput', value: string) {
    if (type === 'audioinput') {
      setSelectedAudioInput(value);
      localStorage.setItem('meetxAudioInput', value);
    } else if (type === 'audiooutput') {
      setSelectedAudioOutput(value);
      localStorage.setItem('meetxAudioOutput', value);
    } else if (type === 'videoinput') {
      setSelectedVideoInput(value);
      localStorage.setItem('meetxVideoInput', value);
    }
  }

  return (
    <div className={styles.settingsOverlay} onClick={onClose}>
      <div className={styles.settingsModal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.settingsHeader}>
          <h2>Settings</h2>
          <button onClick={onClose}>×</button>
        </div>
        <div className={styles.settingsBody}>
          <section className={styles.settingsSection}>
            <h3>Audio</h3>
            <label>Speaker</label>
            <button className={styles.settingsTestBtn} onClick={testSpeaker}>Test Speaker</button>
            <select className={styles.settingsSelect} value={selectedAudioOutput} onChange={e => updateDevice('audiooutput', e.target.value)}>
              <option value="">Default Speaker</option>
              {audioOutputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || 'Speaker'}</option>)}
            </select>
            
            <label>Microphone</label>
            <button className={styles.settingsTestBtn} onClick={testMic}>Test Mic (5s)</button>
            <select className={styles.settingsSelect} value={selectedAudioInput} onChange={e => updateDevice('audioinput', e.target.value)}>
              <option value="">Default Microphone</option>
              {audioInputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || 'Microphone'}</option>)}
            </select>
            <label>Mic Level</label>
            <div className={styles.outputLevel}><span id="mic-level-bar" style={{ width: '0%', transition: 'width 0.1s', background: '#2ecc71', height: '100%', display: 'block' }} /></div>
            
            <label className={styles.settingsCheckRow}>
              <input type="checkbox" checked={ptt} onChange={(e) => togglePtt(e.target.checked)} />
              Hold Space to Speak
            </label>
          </section>
          <section className={styles.settingsSection}>
            <h3>Video</h3>
            <label>Camera</label>
            <select className={styles.settingsSelect} value={selectedVideoInput} onChange={e => updateDevice('videoinput', e.target.value)}>
              <option value="">Default Camera</option>
              {videoInputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || 'Camera'}</option>)}
            </select>
          </section>
        </div>
      </div>
    </div>
  );
}
